// filepath: apps/web/src/editors/bpm-designer/types.ts
//
// FR-UI-BLD-1 AC-3 / AC-4 - wire types for the BPM designer SPA.
//
// The wire types mirror the Go server contract at internal/bpm/handlers.go.
// Node types are the canonical 7 platform-shipped node kinds (FR-BPM-1).
// The FetchError union follows the 5-state fetch contract (FR-UI-CORE-10).

export type NodeType =
  | "start"
  | "end"
  | "userTask"
  | "scriptTask"
  | "xorGateway"
  | "andGateway"
  | "timerCatch";

export const ALL_NODE_TYPES: NodeType[] = [
  "start",
  "end",
  "userTask",
  "scriptTask",
  "xorGateway",
  "andGateway",
  "timerCatch",
];

export interface NodeDefinition {
  node_id: string;
  node_type: NodeType;
  label?: string | null;
  // userTask
  form_definition_id?: string | null;
  candidate_role_id?: string | null;
  assignee_user_id?: string | null;
  due_in_seconds?: number | null;
  // scriptTask
  script_id?: string | null;
  formula?: string | null;
  // timerCatch
  delay_expression?: string | null;
}

export interface EdgeDefinition {
  edge_id: string;
  source_node_id: string;
  target_node_id: string;
  guard_formula?: string | null;
}

export interface ProcessDraft {
  name: string;
  description?: string;
  nodes: NodeDefinition[];
  edges: EdgeDefinition[];
}

export interface ProcessCreateRequest {
  name: string;
  description?: string;
  nodes: NodeDefinition[];
  edges: EdgeDefinition[];
}

export interface ProcessCreateResponse {
  process_id: string;
  version: number;
  name: string;
}

export interface ValidationErrorWire {
  node_id: string;
  type: string;
  field?: string;
  message: string;
}

export interface ValidationErrorEnvelope {
  error: "validation_failed";
  code: "validation_errors";
  errors: ValidationErrorWire[];
}

export interface FormSummary {
  form_id: string;
  version: number;
  entity_type: string;
}

export interface RoleSummary {
  id: string;
  name: string;
  is_system: boolean;
}

export interface ScriptSummary {
  id: string;
  name: string;
}

export interface FormulaValidateRequest {
  formula: string;
  context: "visibleWhen" | "computedFrom" | "guard" | "delay_expression";
  field_id: string;
}

export interface FormulaValidateResponse {
  ok: boolean;
  type?: string;
  error?: string;
}

export type BpmDesignerFetchErrorKind =
  | "not_found"
  | "forbidden"
  | "unauthorized"
  | "network"
  | "validation_failed"
  | "staging_only"
  | "version_conflict"
  | "internal_error";

export interface BpmDesignerFetchError {
  kind: BpmDesignerFetchErrorKind;
  status: number;
  message: string;
  validationErrors?: ValidationErrorWire[];
}

export function classifyBpmDesignerError(
  status: number,
  body: unknown,
): BpmDesignerFetchError {
  if (status === 422) {
    const env = body as ValidationErrorEnvelope;
    return {
      kind: "validation_failed",
      status: 422,
      message: env?.error ?? "validation_failed",
      validationErrors: env?.errors ?? [],
    };
  }
  if (status === 403) {
    const error = (body as { error?: string })?.error ?? "forbidden";
    const code = (body as { code?: string })?.code ?? "forbidden";
    if (code === "ENV_PRODUCTION") {
      return { kind: "staging_only", status: 403, message: error };
    }
    return { kind: "forbidden", status: 403, message: error };
  }
  if (status === 404) {
    return { kind: "not_found", status: 404, message: "not_found" };
  }
  if (status === 401) {
    return { kind: "unauthorized", status: 401, message: "unauthorized" };
  }
  if (status === 0) {
    return { kind: "network", status: 0, message: "network" };
  }
  return {
    kind: "internal_error",
    status,
    message: (body as { error?: string })?.error ?? "internal_error",
  };
}
